import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { FloppyDisk, Eye, Trash, ClockCounterClockwise, CaretRight, ArrowsClockwise } from "@phosphor-icons/react";

const CARD_LAYOUT = [
  { section: "Role Definition", rows: [
    { key: "who_i_am", label: "Who I Am" },
    { key: "role_position_title", label: "Role, Position, Title" },
    { key: "behavior", label: "Behavior" },
    { key: "mode", label: "Mode" },
    { key: "style", label: "Style" },
  ]},
  { section: "AI Logistics", rows: [
    { key: "identity_of_ai", label: "Identity of AI" },
    { key: "ai_engine", label: "AI Engine" },
    { key: "api_key_enabled", label: "API Key Enabled" },
  ]},
  { section: "Continuity Protocol", rows: [
    { key: "continuity_protocol", label: "", area: true },
  ]},
  { section: "Company", rows: [
    { key: "company", label: "Company" },
    { key: "founder", label: "Founder" },
  ]},
  { section: "Team Members", rows: [
    { key: "team_members", label: "", area: true },
  ]},
  { section: "Additional Information", rows: [
    { key: "additional_information", label: "", area: true },
  ]},
];

const CARD_KEYS = CARD_LAYOUT.flatMap((s) => s.rows.map((r) => r.key));
const empty = CARD_KEYS.reduce((a, k) => ({ ...a, [k]: "" }), {});

export default function CardEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [sessionNote, setSessionNote] = useState("");
  const [roles, setRoles] = useState([]);
  const [prid, setPrid] = useState(null);
  const [version, setVersion] = useState(1);
  const [rotation, setRotation] = useState(null);
  const [versions, setVersions] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(isNew);

  useEffect(() => {
    api.get("/meta/roles").then((r) => setRoles(r.data.roles)).catch(() => {});
    if (!isNew) {
      api
        .get(`/cards/${id}`)
        .then((r) => {
          setForm({ ...empty, ...r.data.content });
          setPrid(r.data.prid);
          setVersion(r.data.version);
          setRotation(r.data.rotation);
          setReady(true);
        })
        .catch((err) => {
          toast.error(apiError(err));
          navigate("/dashboard");
        });
      loadVersions();
    }
  }, [id]);

  const loadVersions = () => {
    api.get(`/cards/${id}/versions`).then((r) => setVersions(r.data)).catch(() => {});
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setLoading(true);
    try {
      const payload = { ...form, session_note: sessionNote };
      if (isNew) {
        const { data } = await api.post("/cards", payload);
        toast.success("Card created & encrypted");
        navigate(`/card/${data.id}`);
      } else {
        const { data } = await api.put(`/cards/${id}`, payload);
        setVersion(data.version);
        setRotation(data.rotation);
        setSessionNote("");
        loadVersions();
        toast.success(`Continuity refreshed · v${data.version}`);
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Permanently delete this ID Card and its session history?")) return;
    try {
      await api.delete(`/cards/${id}`);
      toast.success("Card deleted");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!ready) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <p className="pt-28 text-center font-mono-p text-sm text-zinc-600 animate-pulse">DECRYPTING...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 pt-28 pb-24">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-zinc-800 pb-6">
          <div>
            <p className="font-mono-p text-xs tracking-[0.3em] text-cyan-400 mb-2">
              // AI iD Card {isNew ? "· New" : "· Edit"}
            </p>
            {prid && (
              <span className="font-mono-p text-xs tracking-wider text-cyan-400 border border-cyan-400/30 px-2 py-1 inline-flex items-center gap-1.5">
                {prid} · v{version}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {!isNew && (
              <>
                <button
                  data-testid="view-readonly-btn"
                  onClick={() => navigate(`/card/${id}/view`)}
                  className="flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest border border-zinc-700 px-4 py-2.5 text-zinc-300 hover:border-cyan-400/50 hover:text-cyan-400 transition-colors"
                >
                  <Eye size={14} /> Handoff View
                </button>
                <button
                  data-testid="toggle-log-btn"
                  onClick={() => setShowLog(!showLog)}
                  className="flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest border border-zinc-700 px-4 py-2.5 text-zinc-300 hover:border-cyan-400/50 hover:text-cyan-400 transition-colors"
                >
                  <ClockCounterClockwise size={14} /> Log ({versions.length})
                </button>
                <button
                  data-testid="delete-card-btn"
                  onClick={remove}
                  className="flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest border border-zinc-800 px-4 py-2.5 text-zinc-400 hover:border-red-400/50 hover:text-red-400 transition-colors"
                >
                  <Trash size={14} />
                </button>
              </>
            )}
          </div>
        </div>

        {!isNew && rotation && (
          rotation.rotation_due ? (
            <div
              data-testid="rotation-banner-due"
              className="mt-6 border border-amber-400/40 bg-amber-400/5 p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between"
            >
              <div className="flex items-start gap-3">
                <ArrowsClockwise size={20} weight="bold" className="text-amber-400 mt-0.5 shrink-0" />
                <div>
                  <p className="font-mono-p text-xs uppercase tracking-widest text-amber-400">
                    Weekly Rotation Due
                  </p>
                  <p className="text-sm text-zinc-400 mt-1">
                    It's been {Math.floor(rotation.days_since)} day(s) since the last session.
                    Ask your AI to review this card and save a fresh continuity update.
                  </p>
                </div>
              </div>
              <button
                data-testid="refresh-now-btn"
                onClick={() => {
                  setSessionNote("Weekly continuity refresh");
                  document.querySelector('[data-testid="field-session-note"]')?.focus();
                  toast.info("Update the card, then save to refresh continuity");
                }}
                className="shrink-0 font-mono-p text-xs uppercase tracking-widest bg-amber-400 text-black px-4 py-2.5 font-bold hover:bg-amber-300 transition-colors"
              >
                Refresh Now
              </button>
            </div>
          ) : (
            <div
              data-testid="rotation-banner-fresh"
              className="mt-6 border border-zinc-800 bg-[#090A0C] p-4 flex items-center gap-3 text-sm text-zinc-500"
            >
              <ArrowsClockwise size={16} className="text-cyan-400" />
              Continuity is fresh. Next weekly rotation due{" "}
              <span className="text-zinc-300 font-mono-p mx-1">
                {new Date(rotation.next_due_at).toLocaleDateString()}
              </span>{" "}
              ({Math.max(0, Math.ceil(rotation.days_until_due))}d left).
            </div>
          )
        )}

        {showLog && !isNew && (
          <div className="mt-6 border border-zinc-800 bg-[#090A0C] p-6">
            <h3 className="font-mono-p text-xs uppercase tracking-widest text-cyan-400 mb-4">
              Session Log
            </h3>
            <ol className="space-y-4">
              {versions.map((v) => (
                <li key={v.version} className="flex gap-4 border-l border-dashed border-zinc-700 pl-4">
                  <div>
                    <span className="font-mono-p text-sm text-zinc-200">v{v.version}</span>
                    <span className="font-mono-p text-xs text-zinc-600 ml-3">
                      {new Date(v.created_at).toLocaleString()}
                    </span>
                    <p className="text-sm text-zinc-500 mt-1">{v.session_note}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        <div className="mt-8 space-y-6">
          <div data-testid="ai-id-card-doc" className="bg-white text-zinc-900 rounded-lg overflow-hidden border border-zinc-300">
            <div className="grid grid-cols-[minmax(160px,0.9fr)_2fr] bg-zinc-900 text-white font-bold text-sm">
              <div className="px-4 py-3 border-r border-zinc-700">AI iD Card</div>
              <div className="px-4 py-3">Details</div>
            </div>
            {CARD_LAYOUT.map((sec) => (
              <div key={sec.section}>
                <div className="grid grid-cols-[minmax(160px,0.9fr)_2fr] border-t border-zinc-200 bg-zinc-100">
                  <div className="px-4 py-2.5 font-bold text-sm text-zinc-800 border-r border-zinc-200">{sec.section}</div>
                  <div className="px-4 py-2.5" />
                </div>
                {sec.rows.map((r) => (
                  <div key={r.key} className="grid grid-cols-[minmax(160px,0.9fr)_2fr] border-t border-zinc-200">
                    <div className="px-4 py-2.5 text-sm text-zinc-600 border-r border-zinc-200 flex items-start">
                      {r.label}
                    </div>
                    <div className="px-2 py-1">
                      {r.area ? (
                        <textarea
                          data-testid={`field-${r.key}`}
                          value={form[r.key]}
                          onChange={set(r.key)}
                          rows={3}
                          placeholder="—"
                          className="w-full bg-transparent px-2 py-1.5 text-sm text-zinc-900 focus:outline-none focus:bg-blue-50/60 rounded resize-y placeholder:text-zinc-300"
                        />
                      ) : (
                        <input
                          data-testid={`field-${r.key}`}
                          value={form[r.key]}
                          onChange={set(r.key)}
                          placeholder="—"
                          className="w-full bg-transparent px-2 py-1.5 text-sm text-zinc-900 focus:outline-none focus:bg-blue-50/60 rounded placeholder:text-zinc-300"
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="border-t border-zinc-800 pt-6">
            <label className="font-mono-p text-xs uppercase tracking-widest text-cyan-400">
              Session Note {isNew ? "(optional)" : "(what changed this session)"}
            </label>
            <input
              data-testid="field-session-note"
              value={sessionNote}
              onChange={(e) => setSessionNote(e.target.value)}
              placeholder="e.g. Completed auth flow, next: build dashboard"
              className="mt-2 w-full bg-black border border-zinc-800 px-4 py-3 text-zinc-100 text-sm focus:border-cyan-400/60 focus:outline-none transition-colors"
            />
          </div>

          <button
            data-testid="save-card-btn"
            onClick={save}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 font-mono-p text-sm uppercase tracking-widest bg-cyan-400 text-black py-4 font-bold hover:bg-cyan-300 transition-colors disabled:opacity-50"
          >
            <FloppyDisk size={16} weight="bold" />
            {loading ? "Encrypting..." : isNew ? "Create & Encrypt Card" : "Save Session Update"}
            <CaretRight size={16} weight="bold" />
          </button>
        </div>
      </main>
    </div>
  );
}
